import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

export const AutoLogoutHandler = () => {
  useInactivityAutoLogout(
    () => {
      window.location.href = "/logout"; // Redirect to logout page
    },
    process.env.REACT_APP_MAXIMUM_INACTIVE_TIME // Default to 120 minutes if not set
  );
  return null; // no UI
};

export function useInactivityAutoLogout(onLogout, timeoutMs) {
  const timeout = Number(timeoutMs) || 5 * 60 * 1000;
  const timerRef = useRef(null);
  const isLoggedOutRef = useRef(false);
  const activityKey = "user-activity-timestamp";
  const logoutKey = "user-activity-logout";

  const logout = () => {
    if (isLoggedOutRef.current) return;
    isLoggedOutRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    // Clear stale activity so the next login doesn't immediately trigger logout again
    localStorage.removeItem(activityKey);
    localStorage.setItem(logoutKey, Date.now().toString());
    alert("You have been logged out due to inactivity. Please log in again to continue.");
    typeof onLogout === "function" && onLogout();
  };

  const resetTimer = () => {
    localStorage.setItem(activityKey, Date.now().toString());

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(logout, timeout);
  };

  useEffect(() => {
    const now = Date.now();
    const lastActivity = Number(localStorage.getItem(activityKey)) || now;

    if (now - lastActivity > timeout) {
      logout();
      return;
    }

    resetTimer();

    const handleActivity = () => {
      if (!isLoggedOutRef.current) {
        resetTimer();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const last = Number(localStorage.getItem(activityKey)) || Date.now();
        if (Date.now() - last > timeout) {
          logout();
        } else {
          resetTimer();
        }
      }
    };

    const handleStorage = (e) => {
      if (e.key === activityKey && !isLoggedOutRef.current) {
        resetTimer();
      }
      if (e.key === logoutKey) {
        logout(); // logout if another tab logged out
      }
    };

    const events = ["mousemove", "mousedown", "keypress", "scroll", "touchstart"];
    events.forEach((event) => window.addEventListener(event, handleActivity));
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("storage", handleStorage);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach((event) => window.removeEventListener(event, handleActivity));
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("storage", handleStorage);
    };
  }, [onLogout, timeout]);
}
